import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useJobs } from '../contexts/JobContext';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { JobType, UrgencyLevel, JobMedia } from '../types';
import { ArrowLeftIcon, CameraIcon, VideoIcon, XIcon, MapPinIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function RequestJobPage() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    addJob
  } = useJobs();
  const [formData, setFormData] = useState({
    type: 'automotive' as JobType,
    title: '',
    description: '',
    location: '',
    urgency: 'standard' as UrgencyLevel
  });
  const [media, setMedia] = useState<JobMedia[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const handleMediaUpload = (type: 'image' | 'video') => {
    const maxImages = 10;
    const maxVideos = 2;
    const currentImages = media.filter(m => m.type === 'image').length;
    const currentVideos = media.filter(m => m.type === 'video').length;
    if (type === 'image' && currentImages >= maxImages) {
      alert(`Maximum ${maxImages} images allowed`);
      return;
    }
    if (type === 'video' && currentVideos >= maxVideos) {
      alert(`Maximum ${maxVideos} videos allowed`);
      return;
    }
    // Simulate file upload
    const newMedia: JobMedia = {
      id: Date.now().toString(),
      type,
      url: type === 'image' ? `https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop` : 'https://example.com/video.mp4',
      thumbnail: type === 'video' ? `https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=200&h=150&fit=crop` : undefined
    };
    setMedia([...media, newMedia]);
  };
  const removeMedia = (id: string) => {
    setMedia(media.filter(m => m.id !== id));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    addJob({
      customerId: user.id,
      customerName: user.name,
      type: formData.type,
      title: formData.title,
      description: formData.description,
      location: formData.location,
      urgency: formData.urgency,
      status: 'requested',
      media,
      costs: []
    });
    setIsSubmitting(false);
    setShowSuccess(true);
    // Navigate after success animation
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };
  const imageCount = media.filter(m => m.type === 'image').length;
  const videoCount = media.filter(m => m.type === 'video').length;
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-613">
      {/* Success Overlay */}
      <AnimatePresence data-id="element-614">
        {showSuccess && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/90 z-50 flex items-center justify-center" data-id="element-615">
            <motion.div initial={{
          scale: 0,
          rotate: -180
        }} animate={{
          scale: 1,
          rotate: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} className="text-center" data-id="element-616">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 300,
            damping: 15
          }} className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-617">
                <CheckCircleIcon className="w-12 h-12 text-white" data-id="element-618" />
              </motion.div>
              <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="text-3xl font-bold text-white mb-2" data-id="element-619">
                Job Requested!
              </motion.h2>
              <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4
          }} className="text-slate-300" data-id="element-620">
                We'll get back to you soon
              </motion.p>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 sticky top-0 z-10" data-id="element-621">
        <div className="flex items-center gap-4" data-id="element-622">
          <motion.button whileTap={{
          scale: 0.9
        }} onClick={() => navigate('/')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-623">
            <ArrowLeftIcon className="w-6 h-6" data-id="element-624" />
          </motion.button>
          <div data-id="element-625">
            <h1 className="text-2xl font-bold" data-id="element-626">Request a Job</h1>
            <p className="text-slate-300 text-sm" data-id="element-627">Tell us about your project</p>
          </div>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-628">
        {/* Job Type */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} data-id="element-629">
          <label className="block text-sm font-semibold text-slate-700 mb-3" data-id="element-630">
            Job Type
          </label>
          <div className="grid grid-cols-2 gap-3" data-id="element-631">
            {(['automotive', 'industrial', 'residential', 'marine', 'custom'] as JobType[]).map((type, index) => <motion.button key={type} type="button" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.15 + index * 0.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => setFormData({
            ...formData,
            type
          })} className={`p-4 rounded-xl border-2 font-semibold capitalize transition-all ${formData.type === type ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 bg-white text-slate-600'}`} data-id="element-632">
                {type}
              </motion.button>)}
          </div>
        </motion.div>

        {/* Title */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-633">
          <Input label="Job Title" placeholder="e.g., Classic Car Frame Restoration" value={formData.title} onChange={e => setFormData({
          ...formData,
          title: e.target.value
        })} required data-id="element-634" />
        </motion.div>

        {/* Description */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} data-id="element-635">
          <Textarea label="Description" placeholder="Describe the items to be blasted, condition, and any special requirements..." value={formData.description} onChange={e => setFormData({
          ...formData,
          description: e.target.value
        })} rows={4} required data-id="element-636" />
        </motion.div>

        {/* Media Upload */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} data-id="element-637">
          <label className="block text-sm font-semibold text-slate-700 mb-3" data-id="element-638">
            Photos & Videos
          </label>
          <AnimatePresence mode="popLayout" data-id="element-639">
            {media.length > 0 && <motion.div layout className="grid grid-cols-3 gap-3 mb-3" data-id="element-640">
                {media.map((item, index) => <motion.div key={item.id} layout initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} transition={{
              duration: 0.2
            }} className="relative aspect-square bg-slate-200 rounded-lg overflow-hidden" data-id="element-641">
                    <img src={item.type === 'image' ? item.url : item.thumbnail} alt="Upload" className="w-full h-full object-cover" data-id="element-642" />
                    {item.type === 'video' && <div className="absolute inset-0 flex items-center justify-center bg-black/30" data-id="element-643">
                        <VideoIcon className="w-8 h-8 text-white" data-id="element-644" />
                      </div>}
                    <motion.button whileTap={{
                scale: 0.9
              }} type="button" onClick={() => removeMedia(item.id)} className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white" data-id="element-645">
                      <XIcon className="w-4 h-4" data-id="element-646" />
                    </motion.button>
                  </motion.div>)}
              </motion.div>}
          </AnimatePresence>
          <div className="flex gap-3" data-id="element-647">
            <motion.button whileTap={{
            scale: 0.95
          }} type="button" onClick={() => handleMediaUpload('image')} disabled={imageCount >= 10} className="flex-1 p-4 border-2 border-dashed border-slate-300 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-648">
              <CameraIcon className="w-6 h-6 mx-auto mb-2 text-slate-400" data-id="element-649" />
              <p className="text-sm font-semibold text-slate-600" data-id="element-650">
                Add Photos ({imageCount}/10)
              </p>
            </motion.button>
            <motion.button whileTap={{
            scale: 0.95
          }} type="button" onClick={() => handleMediaUpload('video')} disabled={videoCount >= 2} className="flex-1 p-4 border-2 border-dashed border-slate-300 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-651">
              <VideoIcon className="w-6 h-6 mx-auto mb-2 text-slate-400" data-id="element-652" />
              <p className="text-sm font-semibold text-slate-600" data-id="element-653">
                Add Videos ({videoCount}/2)
              </p>
            </motion.button>
          </div>
        </motion.div>

        {/* Location */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} data-id="element-654">
          <Input label="General Location" placeholder="e.g., Austin, TX" value={formData.location} onChange={e => setFormData({
          ...formData,
          location: e.target.value
        })} icon={<MapPinIcon className="w-5 h-5" data-id="element-656" />} required data-id="element-655" />
        </motion.div>

        {/* Urgency */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} data-id="element-657">
          <label className="block text-sm font-semibold text-slate-700 mb-3" data-id="element-658">
            Urgency Level
          </label>
          <div className="space-y-2" data-id="element-659">
            {(['standard', 'urgent', 'emergency'] as UrgencyLevel[]).map((level, index) => <motion.button key={level} type="button" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.45 + index * 0.05
          }} whileTap={{
            scale: 0.98
          }} onClick={() => setFormData({
            ...formData,
            urgency: level
          })} className={`w-full p-4 rounded-xl border-2 font-semibold capitalize text-left transition-all flex items-center gap-3 ${formData.urgency === level ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 bg-white text-slate-600'}`} data-id="element-660">
                  <AlertCircleIcon className="w-5 h-5" data-id="element-661" />
                  <div data-id="element-662">
                    <div className="font-bold" data-id="element-663">{level}</div>
                    <div className="text-xs opacity-70" data-id="element-664">
                      {level === 'standard' && 'Normal turnaround time'}
                      {level === 'urgent' && 'Priority scheduling'}
                      {level === 'emergency' && 'Immediate attention needed'}
                    </div>
                  </div>
                </motion.button>)}
          </div>
        </motion.div>

        {/* Submit */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="pt-4" data-id="element-665">
          <motion.div whileTap={{
          scale: 0.98
        }} data-id="element-666">
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={isSubmitting} data-id="element-667">
              {isSubmitting ? <span className="flex items-center justify-center gap-2" data-id="element-668">
                  <motion.span animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full" data-id="element-669" />
                  Submitting...
                </span> : 'Submit Job Request'}
            </Button>
          </motion.div>
        </motion.div>
      </form>
    </div>;
}